// The composer's compose-feature half, as ONE hook the shared Composer calls (anchored edits there
// stay a few lines): the "/" command strip and execution, the "~" channel strip, the one-shot
// message priority, the ephemeral/error notice above the box, and ↑-to-edit.
//
// Ported from ChannelViewModel.swift (prefetchSlashCommandsIfNeeded, sendMessage's command
// branch, messagePriority) and MacConversationView (SlashCommandStripView / channel-link strip /
// EphemeralNoticeView). Web-only on top: the server's other public channels in the "~" strip, and
// ↑ in an empty box. The "/" strip walks each command's `autocomplete_data` past the first space
// (sub-commands, arguments — shared/API.md §36.2, core/commandAutocomplete) and asks the server only
// for a plugin's DynamicList (§36.3); goto_location is followed (§36.5, ../pluginUi/goto.ts).
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { data } from '../../../bridge/client';
import type { SendExtras } from '../../../bridge/protocol';
import { ChannelLinks, type ChannelLinkActive, type ChannelLinkTarget } from '../../../core/channelLinks';
import { ChannelSuggestions, type ChannelSuggestion } from '../../../core/channelSuggestions';
import * as CommandAutocomplete from '../../../core/commandAutocomplete';
import type { AutocompleteNode, CommandSuggestion } from '../../../core/commandAutocomplete';
import { MessagePriority, type Level } from '../../../core/messagePriority';
import * as SlashCommandComposer from '../../../core/slashCommandComposer';
import type { ComposeConfig } from '../../../data/features/compose';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { MessageBody } from '../../conversation/MessageBody';
import { ShellContext } from '../../shell/ShellContext';
import { followGoto } from '../pluginUi/goto';
import { requestEdit, useChannelCatalogue } from './composeSignals';
import { PriorityButton, PriorityLine } from './Priority';
import { AckLine, NO_ACK, ackMenu, useAckConfig } from '../threads/AckComposer';
import { PostAcknowledgements, type AckChoice } from '../../../core/postAcknowledgements';
import './compose.css';

export type ComposeSuggestion =
  /** `firstLevel`: no space typed yet — rows are triggers, drawn "/trigger" (§15's strip). */
  | { kind: 'command'; items: CommandSuggestion[]; firstLevel: boolean }
  | { kind: 'channel'; active: ChannelLinkActive; items: ChannelSuggestion[] };

/** The strip's highlight rules: a command HINT row can never be highlighted or taken (§36.2). */
export const SuggestionKeys = {
  start(s: { kind: string; items: readonly unknown[] }): number {
    return s.kind === 'command' ? CommandAutocomplete.firstPickable(s.items as CommandSuggestion[]) : 0;
  },
  move(s: { kind: string; items: readonly unknown[] }, from: number, delta: number): number {
    if (s.kind === 'command') return CommandAutocomplete.movePickable(s.items as CommandSuggestion[], from, delta);
    const n = s.items.length;
    return n === 0 ? 0 : (((from + delta) % n) + n) % n;
  },
  /** False = nothing takeable is highlighted: Enter sends as usual. */
  canTake(s: { kind: string; items: readonly unknown[] }, index: number): boolean {
    if (index < 0 || index >= s.items.length) return false;
    return s.kind !== 'command' || CommandAutocomplete.isPickable((s.items as CommandSuggestion[])[index]);
  },
};

type Notice = { tone: 'ephemeral' | 'error'; text: string } | null;

const REMOTE_DEBOUNCE_MS = 200;
let sessionConfig: ComposeConfig | null = null;

const SERVER_DEBOUNCE_MS = 150;

/** A catalogue entry → its tree (a command without `autocomplete_data` is a leaf, §36.1). */
function nodeOf(c: SlashCommandComposer.SlashCommand): AutocompleteNode {
  return c.tree ?? { trigger: c.trigger, hint: c.autoCompleteHint, helpText: c.autoCompleteDesc, roleId: 'system_user', arguments: [], subCommands: [] };
}

let sessionIsAdmin: boolean | null = null;

export function useComposeFeatures(o: {
  channelId: string;
  rootId: string | null;
  text: string;
  cursor: number;
  textarea: RefObject<HTMLTextAreaElement | null>;
  /** Puts text in the box with the caret at `caret` (the Composer's own `place`). */
  place(value: string, caret: number): void;
}) {
  const shell = useContext(ShellContext);
  const teamId = shell?.team.id ?? '';
  const catalogue = useChannelCatalogue();
  const [commands, setCommands] = useState<SlashCommandComposer.SlashCommand[]>([]);
  const commandsFor = useRef('');
  const [remote, setRemote] = useState<{ query: string; items: ChannelLinkTarget[] }>({ query: '', items: [] });
  const [priority, setPriority] = useState<Level | null>(null);
  // §41.3 (features/threads): «Solicitar confirmación» / «Notificar persistentemente», one-shot too.
  const [ack, setAck] = useState<AckChoice>(NO_ACK);
  const ackConfig = useAckConfig();
  const [notice, setNotice] = useState<Notice>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [config, setConfig] = useState<ComposeConfig | null>(sessionConfig);
  const [isAdmin, setIsAdmin] = useState(sessionIsAdmin ?? false);
  /** The server's answer for exactly `text` (§36.3); rows replace the local placeholder. */
  const [server, setServer] = useState<{ text: string; rows: CommandSuggestion[] }>({ text: '', rows: [] });
  const serverBusy = useRef(false);
  const latestText = useRef(o.text);
  latestText.current = o.text;
  const placeRef = useRef(o.place);
  placeRef.current = o.place;

  useEffect(() => {
    if (sessionConfig) return;
    let alive = true;
    void data.call('composeConfig').then((c) => {
      sessionConfig = c;
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  // The catalogue is fetched the first time a draft starts with "/" — not at open: most
  // conversations never type a command (prefetchSlashCommandsIfNeeded).
  const wantsCommands = o.text.startsWith('/');
  useEffect(() => {
    if (!wantsCommands || !teamId || commandsFor.current === teamId) return;
    commandsFor.current = teamId;
    void data.call('slashCommands', teamId).then(setCommands, () => undefined);
    if (sessionIsAdmin === null) {
      void data.call('isSystemAdmin').then((admin) => {
        sessionIsAdmin = admin;
        setIsAdmin(admin);
      }, () => undefined);
    }
  }, [wantsCommands, teamId]);

  // The local walk of the line (§36.2) — rendered at once from the cached catalogue.
  const nodes = useMemo(() => commands.map(nodeOf), [commands]);
  const walked = useMemo(() => (wantsCommands ? CommandAutocomplete.rawSuggestions(nodes, o.text, isAdmin) : null), [wantsCommands, nodes, o.text, isAdmin]);

  // A DynamicList only the server can list (§36.3): 150 ms after the last keystroke, one request in
  // flight, the answer kept only while its text is still the line.
  const needsServer = walked?.needsServer === true;
  useEffect(() => {
    if (!needsServer || !teamId) return;
    const userInput = o.text;
    const ask = () => {
      if (serverBusy.current) return void setTimeout(ask, SERVER_DEBOUNCE_MS);
      if (latestText.current !== userInput) return;
      serverBusy.current = true;
      void data
        .call('commandSuggestions', { teamId, channelId: o.channelId, rootId: o.rootId, userInput })
        .then((rows) => {
          if (rows && latestText.current === userInput) setServer({ text: userInput, rows });
        }, () => undefined)
        .finally(() => {
          serverBusy.current = false;
        });
    };
    const id = setTimeout(ask, SERVER_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [needsServer, o.text, teamId, o.channelId, o.rootId]);

  // The server's other public channels for the "~" query, debounced; the joined tier paints first.
  const channelQuery = ChannelLinks.active(o.text, o.cursor)?.query ?? null;
  useEffect(() => {
    if (channelQuery === null || !teamId) return;
    const id = setTimeout(() => {
      void data.call('channelAutocomplete', teamId, channelQuery).then((items) => setRemote({ query: channelQuery, items }));
    }, REMOTE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [channelQuery, teamId]);

  /** The strip for the caret, or null — called by the Composer AFTER @ and : had their turn. */
  const suggestion = useCallback(
    (text: string, cursor: number): ComposeSuggestion | null => {
      const active = ChannelLinks.active(text, cursor);
      if (text.startsWith('/') && !active) {
        const local = text === o.text ? walked : CommandAutocomplete.rawSuggestions(nodes, text, isAdmin);
        if (!local) return null;
        // A non-empty server answer for this exact line replaces the local placeholder (§36.3).
        const rows = local.needsServer && server.text === text && server.rows.length ? server.rows : local.rows;
        const items = CommandAutocomplete.present(rows, text);
        return items.length ? { kind: 'command', items, firstLevel: !text.includes(' ') } : null;
      }
      if (active) {
        const others = remote.query === active.query || active.query.startsWith(remote.query) ? remote.items : [];
        const items = ChannelSuggestions.suggestions(catalogue.joined, others, active.query);
        return items.length ? { kind: 'channel', active, items } : null;
      }
      return null;
    },
    [walked, nodes, isAdmin, server, o.text, remote, catalogue.joined],
  );

  const accept = useCallback((s: ComposeSuggestion, index: number, text: string, cursor: number): { text: string; cursor: number } => {
    if (s.kind === 'command') {
      const value = CommandAutocomplete.apply(s.items[index]);
      return { text: value, cursor: value.length };
    }
    return ChannelLinks.apply({ text, cursor, active: s.active, slug: s.items[index].slug });
  }, []);

  const renderItems = (s: ComposeSuggestion, highlight: number, pick: (i: number) => void): ReactNode => {
    if (s.kind === 'command') {
      return s.items.map((c, i) => {
        if (!CommandAutocomplete.isPickable(c)) {
          // A HINT row (§36.2): what to type next. Read, never taken — Enter still sends.
          return (
            <div key={`hint-${i}`} className="item compose-command hint-row" role="option" aria-disabled="true" aria-selected={false}>
              <span className="trigger-line">
                <span className="hint">{CommandAutocomplete.rowTitle(c)}</span>
              </span>
              {c.hint && c.description ? <span className="desc">{c.description}</span> : null}
            </div>
          );
        }
        return (
          <button key={c.complete + '|' + c.suggestion} className="item compose-command" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), pick(i))}>
            <span className="trigger-line">
              <span className="trigger">{s.firstLevel ? '/' + c.suggestion : c.suggestion}</span>
              {c.hint ? <span className="hint">{c.hint}</span> : null}
            </span>
            {c.description ? <span className="desc">{c.description}</span> : null}
          </button>
        );
      });
    }
    const out: ReactNode[] = [];
    s.items.forEach((c, i) => {
      if (!c.joined && (i === 0 || s.items[i - 1].joined)) {
        out.push(
          <div key="__others" className="divider">
            {t('web.compose.channels.other')}
          </div>,
        );
      }
      out.push(
        <button key={c.channelId} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), pick(i))}>
          <span className="compose-glyph">
            <Icon name={c.isPrivate ? 'lock' : 'hash'} size={16} />
          </span>
          <span>{c.displayName}</span>
          <span className="sub">~{c.slug}</span>
        </button>,
      );
    });
    return out;
  };

  const go = useCallback(
    async (location: string) => {
      if (!(await followGoto(shell, location, sessionConfig?.siteUrl ?? ''))) setNotice({ tone: 'error', text: t('web.compose.command.failed') });
    },
    [shell],
  );

  /**
   * A leading "/" is a command attempt (shared/API.md §15) — unless it carries files: a command
   * cannot take one, so the reader plainly meant a message (ChannelViewModel.sendMessage).
   * Returns true when the message was taken as a command (the Composer then clears the box).
   */
  const trySend = useCallback(
    (message: string, extras: SendExtras): boolean => {
      if (!SlashCommandComposer.isCommandMessage(message) || (extras.fileIds?.length ?? 0) > 0 || !teamId) return false;
      setNotice(null);
      setRunning(message.split(/\s/)[0]);
      void data.call('executeCommand', { teamId, channelId: o.channelId, rootId: o.rootId, command: message }).then(
        (r) => {
          setRunning(null);
          if (r.kind === 'executed') {
            if (r.ephemeralText && !r.ephemeralShown) setNotice({ tone: 'ephemeral', text: r.ephemeralText });
            if (r.gotoLocation) void go(r.gotoLocation);
            return;
          }
          // Unknown trigger or refusal: the SERVER's sentence (it says how to send a literal "/"),
          // and the words go back in the box if the reader has not started something else.
          setNotice({ tone: 'error', text: r.message || t('web.compose.command.failed') });
          const el = o.textarea.current;
          if (el && el.value === '') placeRef.current(message, message.length);
        },
        () => {
          setRunning(null);
          setNotice({ tone: 'error', text: t('web.compose.command.failed') });
        },
      );
      return true;
    },
    [teamId, o.channelId, o.rootId, o.textarea, go],
  );

  /** Merged into the send's extras; the Composer calls `sent()` after. One-shot, like the Mac. */
  const extras = useCallback((): SendExtras => {
    const wire = PostAcknowledgements.canRequest(ackConfig, o.rootId) ? PostAcknowledgements.wire(priority, ack) : MessagePriority.wire(priority);
    return wire ? { priority: wire } : {};
  }, [priority, ack, ackConfig, o.rootId]);
  const sent = useCallback(() => {
    setPriority(null);
    setAck(NO_ACK);
    setNotice(null);
  }, []);

  /** ↑ in an empty box edits your last message here; true when the key was taken. */
  const keyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, text: string): boolean => {
      if (e.key !== 'ArrowUp' || text !== '' || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey || e.nativeEvent.isComposing) return false;
      e.preventDefault();
      const returnFocus = o.textarea.current;
      void data.call('lastEditablePost', o.channelId, o.rootId).then((postId) => {
        if (postId) requestEdit(postId, o.channelId, !!o.rootId, returnFocus);
      });
      return true;
    },
    [o.channelId, o.rootId, o.textarea],
  );

  const priorityAvailable = config ? config.postPriority : true;
  const toolbar = priorityAvailable ? <PriorityButton value={priority} onChange={setPriority} ack={ackMenu(ackConfig, priority, o.rootId, ack, setAck)} /> : null;

  const above: ReactNode = (
    <>
      {running ? <div className="compose-running">{tf('web.compose.command.running', running)}</div> : null}
      {notice ? (
        <div className={`compose-notice ${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
          <div className="text">
            {notice.tone === 'ephemeral' ? <div className="only-you">{t('channel.ephemeral.onlyYou')}</div> : null}
            {notice.tone === 'ephemeral' ? <MessageBody message={notice.text} ctx={NOTICE_CTX} /> : notice.text}
          </div>
          <button type="button" onClick={() => setNotice(null)}>
            {t('channel.ephemeral.dismiss')}
          </button>
        </div>
      ) : null}
    </>
  );

  /** Goes INSIDE the box, above the field (the Mac's composerFieldIdle line). */
  const fieldLine = priorityAvailable && (priority || ack.requestAck || ack.persistent) ? (
    <>
      {priority ? <PriorityLine value={priority} onClear={() => setPriority(null)} /> : null}
      {PostAcknowledgements.canRequest(ackConfig, o.rootId) ? <AckLine value={ack} level={priority} onClear={() => setAck(NO_ACK)} /> : null}
    </>
  ) : null;

  return useMemo(
    () => ({ suggestion, accept, renderItems, trySend, extras, sent, keyDown, toolbar, above, fieldLine }),
    // renderItems/toolbar/above are cheap render helpers; recomputed with the state they read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestion, accept, trySend, extras, sent, keyDown, priority, priorityAvailable, notice, running, ack, ackConfig],
  );
}

const NOTICE_CTX = { knownUsernames: new Set<string>(), currentUsername: null, customEmoji: {} };
